/**
 * @fileoverview Estilos da área Equipe e permissões, nos tokens das
 * Configurações: tons neutros, cor só no estado (concedida e negada), anel de
 * foco de 1 px e as mesmas linhas e cartões das outras seções.
 */

import { makeStyles } from "@material-ui/core/styles";

import { modernFieldStyles } from "../ModernSelect";
import { tokens } from "../Triage/styles";

// Pessoa, perfil, personalizações, licença, filas e último acesso (a data).
const PEOPLE_COLUMNS = "minmax(180px, 1.5fr) 112px minmax(104px, 1fr) minmax(110px, 1fr) minmax(90px, 0.8fr) 116px";

const useTeamAccessStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const xs = theme.breakpoints.down("xs");
  const sm = theme.breakpoints.down("sm");
  const focus = { outline: "none", boxShadow: `0 0 0 1px ${dark ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.6)"}` };
  const button = {
    height: 34,
    padding: "0 12px",
    borderRadius: 8,
    border: `1px solid ${c.inputBorder}`,
    background: "transparent",
    color: c.textPrimary,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 120ms",
    "&:hover:not(:disabled)": { background: c.hover },
    "&:disabled": { opacity: 0.55, cursor: "default" },
    "&:focus-visible": focus,
  };

  return {
    root: { display: "flex", flexDirection: "column", gap: 16, color: c.textPrimary },

    // Abas: a linha de baixo e o sublinhado da ativa são sombras por dentro (o
    // sublinhado cobre a linha sem passar da barra). Com uma borda e margem
    // negativa, ele vazava 1 px e a barra ganhava rolagem vertical. Num celular
    // estreito, a barra rola para o lado, sem barra de rolagem à vista.
    tabs: {
      display: "flex",
      gap: 4,
      boxShadow: `inset 0 -1px 0 ${c.border}`,
      overflowX: "auto",
      overflowY: "hidden",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
    tab: {
      height: 38,
      padding: "0 12px",
      border: "none",
      background: "transparent",
      color: c.textSecondary,
      fontSize: 13.5,
      fontWeight: 500,
      fontFamily: "inherit",
      cursor: "pointer",
      whiteSpace: "nowrap",
      "&:hover": { color: c.textPrimary },
      "&:focus-visible": {
        outline: `1px solid ${dark ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.6)"}`,
        outlineOffset: -1,
      },
    },
    tabActive: { color: c.textPrimary, boxShadow: `inset 0 -2px 0 ${c.fill}`, fontWeight: 600 },

    // Barra de busca e filtros
    toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    search: {
      ...modernFieldStyles(theme),
      flex: "1 1 220px",
      minWidth: 180,
      [xs]: { flexBasis: "100%" },
    },
    filterSelect: { minWidth: 168, [xs]: { flex: "1 1 140px", minWidth: 0 } },
    toggleChip: {
      ...button,
      height: 36,
      borderRadius: 999,
      color: c.textSecondary,
    },
    toggleChipOn: { color: c.textPrimary, background: c.hover, borderColor: c.textMuted },

    // Cartão com linhas
    card: { background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, overflow: "hidden" },
    muted: { color: c.textMuted, fontSize: 12.5 },
    empty: { padding: "28px 20px", textAlign: "center", fontSize: 13, color: c.textSecondary },
    secondaryButton: button,
    primaryButton: {
      ...button,
      border: `1px solid ${c.fill}`,
      background: c.fill,
      color: dark ? "#0f172a" : "#ffffff",
      fontWeight: 600,
      "&:hover:not(:disabled)": { opacity: 0.9, background: c.fill },
    },
    linkButton: {
      border: "none",
      background: "transparent",
      padding: 0,
      color: c.textSecondary,
      fontSize: 13,
      fontFamily: "inherit",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      "&:hover": { color: c.textPrimary },
      "&:focus-visible": focus,
    },

    // Pessoas
    peopleHead: {
      display: "grid",
      gridTemplateColumns: PEOPLE_COLUMNS,
      gap: 12,
      padding: "10px 16px",
      borderBottom: `1px solid ${c.border}`,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: c.textMuted,
      [sm]: { display: "none" },
    },
    personRow: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: PEOPLE_COLUMNS,
      gap: 12,
      alignItems: "center",
      padding: "12px 16px",
      border: "none",
      borderTop: `1px solid ${c.border}`,
      background: "transparent",
      color: c.textPrimary,
      textAlign: "left",
      fontFamily: "inherit",
      fontSize: 13,
      cursor: "pointer",
      "&:first-child": { borderTop: "none" },
      "&:hover": { background: c.hover },
      "&:focus-visible": { ...focus, position: "relative" },
      [sm]: { gridTemplateColumns: "1fr", gap: 4, padding: "12px 14px" },
    },
    personCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F5",
      color: dark ? "#A1A1AA" : "#52525B",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      objectFit: "cover",
    },
    avatarSmall: { width: 24, height: 24, fontSize: 10 },
    nameWrap: { minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    name: { fontWeight: 600, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    email: { fontSize: 12, color: c.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    cellText: {
      fontSize: 12.5,
      color: c.textSecondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    // No celular, cada informação vem com o nome da coluna.
    cellLabel: { display: "none", color: c.textMuted, marginRight: 6, [sm]: { display: "inline" } },
    rolePill: {
      display: "inline-flex",
      alignItems: "center",
      height: 22,
      padding: "0 8px",
      borderRadius: 999,
      border: `1px solid ${c.border}`,
      fontSize: 12,
      fontWeight: 500,
      color: c.textPrimary,
      whiteSpace: "nowrap",
      width: "fit-content",
    },

    // Relendo a página: a de antes fica à vista, mais apagada.
    refreshing: { opacity: 0.55, transition: "opacity 120ms" },
    pager: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 12,
      padding: "8px 12px 8px 16px",
      borderTop: `1px solid ${c.border}`,
      fontSize: 12.5,
      color: c.textSecondary,
    },
    pagerButtons: { display: "inline-flex", gap: 2 },

    // Estados: só concedida e negada têm cor.
    granted: { color: c.ok },
    denied: { color: c.bad },

    // Painel da pessoa
    panelHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      padding: 16,
    },
    panelWho: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    panelControls: { display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap", [xs]: { width: "100%" } },
    roleField: { minWidth: 200, [xs]: { flex: 1, minWidth: 0 } },
    roleHelp: { padding: "0 16px 14px", fontSize: 12.5, color: c.textSecondary, lineHeight: 1.5 },
    notice: {
      padding: "10px 14px",
      borderRadius: 10,
      border: `1px solid ${c.border}`,
      background: c.subtle,
      fontSize: 13,
      color: c.textSecondary,
      lineHeight: 1.5,
    },
    noticeWarn: { borderColor: c.warnBorder, background: c.warnBg, color: c.warn },
    group: { background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, overflow: "hidden" },
    groupHead: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 16px",
      border: "none",
      background: "transparent",
      color: c.textPrimary,
      fontFamily: "inherit",
      fontSize: 14,
      fontWeight: 600,
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { background: c.hover },
      "&:focus-visible": focus,
    },
    groupTitle: { flex: 1, minWidth: 0 },
    groupCount: { fontSize: 12, fontWeight: 500, color: c.textMuted },
    chevron: { color: c.textMuted, transition: "transform 160ms", flexShrink: 0 },
    chevronOpen: { transform: "rotate(90deg)" },
    permissionRow: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "12px 16px",
      borderTop: `1px solid ${c.border}`,
      [xs]: { flexWrap: "wrap", gap: 8 },
    },
    permissionText: { flex: 1, minWidth: 0 },
    permissionTitleLine: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    permissionTitle: { fontSize: 13.5, fontWeight: 600, color: c.textPrimary, lineHeight: 1.4 },
    permissionHelp: { marginTop: 2, fontSize: 12.5, color: c.textSecondary, lineHeight: 1.5 },
    permissionMeta: { marginTop: 4, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12 },
    sensitiveTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      height: 20,
      padding: "0 6px",
      borderRadius: 6,
      border: `1px solid ${c.border}`,
      fontSize: 11,
      fontWeight: 500,
      color: c.textSecondary,
    },
    origin: { color: c.textMuted },
    lockReason: { display: "inline-flex", alignItems: "center", gap: 4, color: c.textMuted },
    permissionControl: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, [xs]: { marginLeft: "auto" } },
    scopeSelect: { minWidth: 168 },
    iconButton: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: c.textMuted,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      "&:hover:not(:disabled)": { background: c.hover, color: c.textPrimary },
      "&:disabled": { opacity: 0.4, cursor: "default" },
      "&:focus-visible": focus,
    },
    footer: {
      position: "sticky",
      bottom: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 10,
      flexWrap: "wrap",
      padding: "12px 16px",
      borderRadius: 12,
      border: `1px solid ${c.border}`,
      background: c.surface,
      boxShadow: dark ? "0 -6px 18px rgba(0,0,0,0.35)" : "0 -6px 18px rgba(15,23,42,0.06)",
    },
    footerMessage: { flex: "1 1 200px", fontSize: 12.5, color: c.warn, lineHeight: 1.5 },
    spacer: { flex: 1 },

    // Confirmação e histórico
    changeList: { margin: "6px 0 12px", paddingLeft: 18, fontSize: 13, lineHeight: 1.6 },
    changeHeading: { margin: "10px 0 0", fontSize: 12, fontWeight: 600, color: c.textMuted, textTransform: "uppercase" },

    // Perfis de acesso (matriz)
    matrix: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    matrixHead: {
      textAlign: "left",
      padding: "10px 12px",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: c.textMuted,
      borderBottom: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    },
    // O grupo: uma faixa com fundo próprio e o nome em versalete, como o
    // cabeçalho das colunas; as permissões dele vêm recuadas logo abaixo.
    matrixGroup: {
      padding: "9px 16px",
      textAlign: "left",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: c.textSecondary,
      background: dark ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.045)",
      borderBottom: `1px solid ${c.border}`,
    },
    matrixCell: {
      padding: "9px 12px",
      borderBottom: `1px solid ${c.border}`,
      color: c.textPrimary,
      verticalAlign: "middle",
    },
    matrixRowHead: { fontWeight: 400, textAlign: "left", paddingLeft: 28 },
    matrixMark: { textAlign: "center", width: 112, color: c.textSecondary },
    // Incluída em verde (o estado); não incluída, um traço neutro e apagado:
    // não ter a permissão é o padrão do perfil, não um erro.
    matrixYes: { color: c.ok },
    matrixNo: { color: c.textMuted, opacity: 0.7 },
    matrixScroll: { overflowX: "auto" },

    historyRow: { padding: "12px 16px", borderTop: `1px solid ${c.border}`, "&:first-child": { borderTop: "none" } },
    historyTop: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
    historyWhat: { fontSize: 13.5, fontWeight: 600 },
    historyWhen: { fontSize: 12, color: c.textMuted, whiteSpace: "nowrap" },
    historyWho: { marginTop: 2, fontSize: 12.5, color: c.textSecondary },
    historyChanges: { margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5, color: c.textSecondary, lineHeight: 1.6 },
    dateField: { ...modernFieldStyles(theme), width: 168, [xs]: { flex: "1 1 140px", width: "auto" } },
    // O filtro por pessoa do histórico: campo com busca, com o menu do select padrão.
    personPicker: {
      width: 260,
      [xs]: { width: "100%" },
      "& $dateField": { width: "100%" },
    },
    pickerPaper: {
      marginTop: 8,
      borderRadius: 14,
      background: dark ? "linear-gradient(135deg, #121213 0%, #18181b 100%)" : "#ffffff",
      border: dark ? "1px solid rgba(91, 124, 255, 0.18)" : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow: dark ? "0 18px 40px rgba(0, 0, 0, 0.4)" : "0 18px 40px rgba(15, 23, 42, 0.12)",
      color: c.textPrimary,
      fontSize: "0.9rem",
      "& .MuiAutocomplete-listbox": { padding: 6 },
      "& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading": { color: c.textSecondary, fontSize: 13 },
    },
    pickerPerson: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    pickerName: {
      fontSize: 13.5,
      fontWeight: 500,
      color: c.textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerInputAvatar: { display: "inline-flex", marginLeft: 6, marginRight: 2 },
    pickerHint: { padding: "8px 14px 10px", borderTop: `1px solid ${c.border}`, fontSize: 12, color: c.textMuted },
    pickerOption: {
      borderRadius: 10,
      minHeight: 36,
      '&[data-focus="true"]': { background: dark ? "rgba(91, 124, 255, 0.16)" : "rgba(61, 92, 253, 0.08)" },
      '&[aria-selected="true"]': { background: dark ? "rgba(91, 124, 255, 0.22)" : "rgba(61, 92, 253, 0.12)" },
    },
    loadMore: { display: "flex", justifyContent: "center", padding: 12 },
  };
});

export default useTeamAccessStyles;
